import React, { useState, useMemo } from 'react';
import {
  FiCalendar,
  FiClock,
  FiUser,
  FiChevronLeft,
  FiChevronRight,
  FiCircle,
  FiCheckCircle,
  FiPlay,
  FiUsers
} from 'react-icons/fi';

const TaskTimelineView = ({ tasks, onTaskClick, canEdit }) => {
  const [viewMode, setViewMode] = useState('weekly'); // 'daily', 'weekly', 'someday'
  const [currentDate, setCurrentDate] = useState(new Date());

  const statusConfig = {
    pending: { icon: FiCircle, color: 'text-gray-500' },
    in_progress: { icon: FiPlay, color: 'text-blue-500' },
    completed: { icon: FiCheckCircle, color: 'text-green-500' },
    delegated: { icon: FiUsers, color: 'text-purple-500' }
  };

  const priorityBadge = {
    urgent: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    low: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
  };

  // Generate date range based on view mode
  const dateRange = useMemo(() => {
    const dates = [];
    const start = new Date(currentDate);

    if (viewMode === 'daily') {
      start.setHours(0, 0, 0, 0);
      dates.push(new Date(start));
    } else if (viewMode === 'weekly') {
      const day = start.getDay();
      const diff = start.getDate() - day + (day === 0 ? -6 : 1);
      start.setDate(diff);
      start.setHours(0, 0, 0, 0);

      for (let i = 0; i < 7; i++) {
        dates.push(new Date(start));
        start.setDate(start.getDate() + 1);
      }
    }

    return dates;
  }, [currentDate, viewMode]);

  const tasksByDate = useMemo(() => {
    const grouped = {};

    dateRange.forEach(date => {
      const dateKey = date.toLocaleDateString('en-CA');
      grouped[dateKey] = [];
    });

    grouped['someday'] = [];

    tasks.forEach(task => {
      if (task.scheduled_date) {
        const taskDate = new Date(task.scheduled_date);
        const dateKey = taskDate.toLocaleDateString('en-CA');
        if (grouped[dateKey]) {
          grouped[dateKey].push(task);
        }
      } else if (task.deadline_date) {
        const taskDate = new Date(task.deadline_date);
        const dateKey = taskDate.toLocaleDateString('en-CA');
        if (grouped[dateKey]) {
          grouped[dateKey].push(task);
        }
      } else if (viewMode === 'someday') {
        grouped['someday'].push(task);
      }
    });

    return grouped;
  }, [tasks, dateRange, viewMode]);

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);

    if (viewMode === 'daily') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (viewMode === 'weekly') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    }

    setCurrentDate(newDate);
  };

  const TaskCard = ({ task }) => {
    const StatusIcon = statusConfig[task.status]?.icon || FiCircle;

    return (
      <div
        onClick={() => onTaskClick(task)}
        className="group bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/80 border border-gray-200 dark:border-gray-700 rounded-md p-2 cursor-pointer transition-colors"
        style={{
          borderLeftColor: task.color || '#3b82f6',
          borderLeftWidth: '3px'
        }}
      >
        <div className="flex items-start justify-between gap-1.5 mb-1">
          <div className="flex-1 min-w-0">
            <h4 className="text-xs font-medium text-gray-900 dark:text-white leading-tight">
              {task.title}
            </h4>
            {task.description && (
              <p className="text-[11px] text-gray-600 dark:text-gray-400 line-clamp-2 mt-0.5">
                {task.description}
              </p>
            )}
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <StatusIcon className={`w-3 h-3 ${statusConfig[task.status]?.color || 'text-gray-500'}`} />
            {task.priority && task.priority !== 'medium' && (
              <span className={`px-1 py-0.5 text-[9px] rounded font-semibold uppercase ${priorityBadge[task.priority]}`}>
                {task.priority}
              </span>
            )}
          </div>
        </div>

        {/* Meta */}
        <div className="flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-400 mt-1.5">
          <div className="flex items-center gap-2">
            {task.assignee && (
              <div className="flex items-center gap-0.5">
                <FiUser className="w-2.5 h-2.5" />
                <span className="truncate max-w-[50px]">{task.assignee.firstName}</span>
              </div>
            )}
            {task.estimated_hours && (
              <div className="flex items-center gap-0.5">
                <FiClock className="w-2.5 h-2.5" />
                <span>{task.estimated_hours}h</span>
              </div>
            )}
          </div>

          {task.deadline_date && (
            <div className="flex items-center gap-0.5 text-orange-600 dark:text-orange-400 font-medium">
              <FiCalendar className="w-2.5 h-2.5" />
              <span>{new Date(task.deadline_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
            </div>
          )}
        </div>

        {/* Tags */}
        {task.tags && task.tags.length > 0 && (
          <div className="flex flex-wrap gap-0.5 mt-1">
            {task.tags.slice(0, 2).map(tag => (
              <span
                key={tag.id}
                className="px-1 py-0 text-[9px] rounded font-medium"
                style={{
                  backgroundColor: `${tag.color}25`,
                  color: tag.color
                }}
              >
                {tag.name}
              </span>
            ))}
            {task.tags.length > 2 && (
              <span className="px-1 py-0 text-[9px] rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-medium">
                +{task.tags.length - 2}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const formatDateHeader = (date) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else if (viewMode === 'daily') {
      return date.toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      });
    } else {
      return date.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric'
      });
    }
  };

  return (
    <div className="space-y-3">
      {/* Timeline Controls */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          {/* View Mode Selector */}
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-md p-0.5">
            {['daily', 'weekly', 'someday'].map(mode => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-colors capitalize ${
                  viewMode === mode
                    ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>

          {/* Date Navigation */}
          {viewMode !== 'someday' && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigateDate(-1)}
                className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <FiChevronLeft className="w-4 h-4" />
              </button>

              <div className="text-center min-w-[160px]">
                <h3 className="font-medium text-sm text-gray-900 dark:text-white">
                  {viewMode === 'daily'
                    ? formatDateHeader(currentDate)
                    : `Week of ${dateRange[0]?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
                  }
                </h3>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  {currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                </p>
              </div>

              <button
                onClick={() => navigateDate(1)}
                className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Today Button */}
          {viewMode !== 'someday' && (
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-2.5 py-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
            >
              Today
            </button>
          )}
        </div>
      </div>

      {/* Timeline Content */}
      {viewMode === 'someday' ? (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-4">
          <div className="flex items-center gap-2 mb-3">
            <FiCalendar className="w-4 h-4 text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Someday / Maybe
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              ({tasksByDate['someday']?.length || 0} tasks)
            </span>
          </div>

          <div className="space-y-2">
            {tasksByDate['someday']?.map(task => (
              <TaskCard key={task.id} task={task} />
            ))}

            {(!tasksByDate['someday'] || tasksByDate['someday'].length === 0) && (
              <div className="text-center py-6 text-gray-400 dark:text-gray-500">
                <FiCalendar className="w-6 h-6 mx-auto mb-1" />
                <p className="text-xs">No unscheduled tasks</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={`grid gap-2 ${viewMode === 'weekly' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7' : 'grid-cols-1'}`}>
          {dateRange.map(date => {
            const dateKey = date.toLocaleDateString('en-CA');
            const dayTasks = tasksByDate[dateKey] || [];

            return (
              <div key={dateKey} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-2 min-h-[200px] flex flex-col">
                <div className="flex items-center justify-between mb-2 flex-shrink-0">
                  <h3 className="font-medium text-xs text-gray-900 dark:text-white truncate">
                    {formatDateHeader(date)}
                  </h3>
                  <span className="text-[10px] text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0 rounded-full flex-shrink-0">
                    {dayTasks.length}
                  </span>
                </div>

                <div className="space-y-1.5 flex-1 overflow-y-auto">
                  {dayTasks.map(task => (
                    <TaskCard key={task.id} task={task} />
                  ))}

                  {(!dayTasks || dayTasks.length === 0) && (
                    <div className="text-center py-3 text-gray-400 dark:text-gray-500 flex-1 flex flex-col items-center justify-center">
                      <FiCalendar className="w-4 h-4 mb-1 opacity-50" />
                      <p className="text-[10px]">No tasks</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TaskTimelineView;
